import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import store from "./store";
import { redactSecrets } from "./utils/telemetryRedaction";
import { isAnalyticsSuppressed } from "./utils/urlHandoff";

const isProd = process.env.REACT_APP_SERVER_TYPE === "prod";
const postHogKey = process.env.REACT_APP_PUBLIC_POSTHOG_KEY as string;

// GOD mode (admin login-as-user): the early inline script sets this flag on the
// /impersonate handoff page and on any tab holding an imp_ token, so trackers
// never initialize during impersonation.
const inGodMode = (): boolean =>
  typeof window !== "undefined" && (window as { __NUDGE_GOD_MODE__?: boolean }).__NUDGE_GOD_MODE__ === true;

// Credentials (bearer tokens in Authorization headers, `?access=` / `?token=` in URLs) are
// redacted from every outgoing Sentry event, transaction and breadcrumb, and from every PostHog
// event, by `redactSecrets`. The URL ones are stripped by index.html before either SDK loads;
// this is defence in depth (Navigation Timing still reports the original URL).

// index.html sets `__nudgeSuppressAnalytics` when it could not strip a credential off the URL: no
// PostHog for that page load (it would record the location).
if (isProd && !inGodMode() && !isAnalyticsSuppressed()) {
  posthog.init(postHogKey, {
    api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
    // Disable session recordings cos we're already using Clarity and Sentry
    // Reference: https://posthog.com/docs/session-replay/how-to-control-which-sessions-you-record
    disable_session_recording: true,
    // Defaults to "if_capture_pageview" (i.e. on); we don't use bounce/duration.
    capture_pageleave: false,
    // Autocapture is already disabled via PostHog dashboard settings
    before_send: (event) => {
      if (!event) {
        return event;
      }
      try {
        event.properties = redactSecrets(event.properties) as typeof event.properties;
        if (event.$set) event.$set = redactSecrets(event.$set) as typeof event.$set;
        if (event.$set_once) event.$set_once = redactSecrets(event.$set_once) as typeof event.$set_once;
      } catch {
        // never let the scrubber itself drop telemetry
      }
      if (process.env.REACT_APP_VERSION) {
        event.properties = {
          ...event.properties,
          app_version: process.env.REACT_APP_VERSION,
        };
      }
      return event;
    },
  });
}

Sentry.init({
  dsn: "https://068f74177d2925c91249ba816ca09ab6@o4506998898688000.ingest.us.sentry.io/4507005146759168",
  environment: process.env.REACT_APP_SERVER_TYPE,
  release: process.env.REACT_APP_VERSION,
  integrations: [
    Sentry.browserTracingIntegration(),
    // error only: console.warn still lands as a breadcrumb on real events, but
    // no longer creates standalone issues — warn-level noise (library
    // deprecations, per-chunk degraded-path logs) was burying actionable
    // errors, and every intentional warn-path already has a bounded
    // Sentry.capture* or product event of its own.
    Sentry.captureConsoleIntegration({
      levels: ["error"],
    }),
  ],
  beforeSend: (event) => {
    try {
      return redactSecrets(event) as typeof event;
    } catch {
      return event;
    }
  },
  beforeSendTransaction: (event) => {
    try {
      return redactSecrets(event) as typeof event;
    } catch {
      return event;
    }
  },
  beforeBreadcrumb: (breadcrumb) => {
    try {
      return redactSecrets(breadcrumb) as typeof breadcrumb;
    } catch {
      return breadcrumb;
    }
  },
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["localhost", /^\//],
  ignoreErrors: [
    // Axios request cancellations (e.g. when the user switches clients/pages mid-fetch)
    // are intentional aborts, not bugs. They were the top noise source in Sentry.
    "CanceledError",
    "AbortError",
    "Request aborted",
    "canceled",
    "google.accounts.id.initialize() is called multiple times",
  ],
});

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <PostHogProvider client={posthog}>
    <Provider store={store}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Provider>
  </PostHogProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
//
reportWebVitals();
